import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import {
  BrowserAuthErrorCodes,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
  PublicClientApplication,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { config } from '@/config/env';
import { AuthContext, type AuthState } from './AuthContext';
import { loginRequest, msalConfiguration } from './msalConfig';

let instancia: PublicClientApplication | null = null;
let redireccioEnCurs = false;

function esInteraccioEnCurs(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    'errorCode' in e &&
    (e as { errorCode?: string }).errorCode === BrowserAuthErrorCodes.interactionInProgress
  );
}

/** Una sola redirecció interactiva alhora: les peticions concurrents que reben 401 no en llancen més. */
async function redireccioUnica(fer: () => Promise<void>): Promise<void> {
  if (redireccioEnCurs) return;
  redireccioEnCurs = true;
  try {
    await fer();
  } catch (e) {
    if (!esInteraccioEnCurs(e)) {
      redireccioEnCurs = false;
      console.error('[auth] redirecció MSAL fallida', e);
    }
  }
}

function obteInstancia(): PublicClientApplication {
  if (!instancia) {
    instancia = new PublicClientApplication(msalConfiguration(config));
    instancia.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const resultat = event.payload as AuthenticationResult;
        instancia?.setActiveAccount(resultat.account);
      }
    });
  }
  return instancia;
}

function Pont({ children }: { children: ReactNode }) {
  const { instance, inProgress, accounts } = useMsal();
  const autenticat = useIsAuthenticated();
  const compte = instance.getActiveAccount() ?? accounts[0] ?? null;

  useEffect(() => {
    if (!instance.getActiveAccount() && accounts[0]) instance.setActiveAccount(accounts[0]);
  }, [instance, accounts]);

  const login = useCallback(async () => {
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    const peticio = loginRequest(config);
    await redireccioUnica(() =>
      account
        ? instance.acquireTokenRedirect({ scopes: peticio.scopes, account })
        : instance.loginRedirect(peticio),
    );
  }, [instance]);

  const logout = useCallback(async () => {
    await instance.logoutRedirect({ account: compte ?? undefined });
  }, [instance, compte]);

  const obteToken = useCallback(async () => {
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) return null;
    const peticio = { scopes: loginRequest(config).scopes, account };
    try {
      const resultat = await instance.acquireTokenSilent(peticio);
      return resultat.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        await redireccioUnica(() => instance.acquireTokenRedirect(peticio));
      } else if (!esInteraccioEnCurs(e)) {
        // Errors no interactius (xarxa, temps d'espera de l'iframe, memòria cau…): la petició
        // surt sense token i, si l'API respon 401, onNoAutenticat torna a iniciar la sessió.
        console.warn('[auth] token silenciós no disponible', e);
      }
      return null;
    }
  }, [instance]);

  const valor = useMemo<AuthState>(
    () => ({
      mode: 'msal',
      autenticat,
      carregant: inProgress !== InteractionStatus.None,
      compte: compte ? { nom: compte.name ?? compte.username, upn: compte.username } : null,
      login,
      logout,
      obteToken,
    }),
    [autenticat, inProgress, compte, login, logout, obteToken],
  );

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function MsalAuthProvider({ children }: { children: ReactNode }) {
  return (
    <MsalProvider instance={obteInstancia()}>
      <Pont>{children}</Pont>
    </MsalProvider>
  );
}
